/**
 * The prose an agent wrote for a section that is now a typed field, shown read-only under the
 * widget so the briefer can read it and copy what matters into the field.
 *
 * How a section reaches this state: brief generation emits `content` only (`buildBriefSections`
 * writes no `value` and no `type`), and brief creation stamps the source template's `type` onto
 * the section afterwards. The section then has a real type, a null `value`, and the model's
 * text in `content` - which no widget renders, so without this the text is invisible here even
 * though it is still on the wire.
 *
 * DISAPPEARANCE RULE: rendered only while the widget's `value` is still empty. The moment the
 * field has an answer, that answer is the section's single source of truth and this note goes -
 * a brief that showed both would carry two competing answers for one section, with nothing
 * saying which one counts. Nothing is destroyed at that moment: `content` is left untouched, so
 * clearing the field brings the note back. Anything that makes this block outlive a filled
 * value reintroduces exactly the ambiguity it exists to avoid.
 */
import { EditorContent } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { BRIEF_PROSE_CLASSES, useBriefSectionViewer } from '@/entities/brief';

// Shares BRIEF_PROSE_CLASSES with the live editor and the read-only previews so the agent's
// text reads the same wherever it appears; read-only needs no tall click target.
const PROSE_OVERRIDES = `${BRIEF_PROSE_CLASSES} [&_.ProseMirror]:min-h-[24px]`;

interface AgentProseNoteProps {
  /** The section's TipTap `content`, unparsed - the viewer narrows it. */
  content: unknown;
}

export function AgentProseNote({ content }: AgentProseNoteProps) {
  const { t } = useTranslation('projects');
  const editor = useBriefSectionViewer(content);

  // Structurally-malformed content: the section still has its widget, so say nothing rather
  // than putting an error under a field that works.
  if (!editor) return null;

  return (
    <div
      role="note"
      data-testid="brief-section-agent-prose"
      className="flex flex-col gap-2xs rounded-lg border border-dashed border-border bg-muted p-sm"
    >
      <p className="text-body-sm font-medium text-muted-foreground">
        {t('projectBriefDetail.briefSectionAgentProse.writtenByAnAgentBefore')}
      </p>
      <EditorContent editor={editor} className={PROSE_OVERRIDES} />
      <p className="text-body-sm text-muted-foreground">
        {t('projectBriefDetail.briefSectionAgentProse.copyWhatYouNeedInto')}
      </p>
    </div>
  );
}
